import React, { useMemo, useState, Dispatch, SetStateAction } from "react";
import { Form, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@core/components/ui/accordion";
import { Button } from "@core/components/ui/button";
import { useToast } from "@core/components/ui/use-toast";
import { useLocation, useNavigate } from "@remix-run/react";
import { useDialog } from "@core/components/ui/use-dialog";
import { cn } from "@core/lib/utils";
import Spinner from "@phoenix/core/components/ui/Spinner";

interface EditorFrameProps extends React.PropsWithChildren {
  title: string;
  subtitle?: string;
  onSave: (...args: any[]) => any;
  onCancel?: () => void;
  defaultValues?: { [x: string]: any } | undefined;
  classNames?: string;
  AdditionalButtons?: () => JSX.Element;
  deleteDialogNote?: React.ReactNode;
  hideDelete?: boolean;
  disableDefaultRedirect?: boolean;
  hideSave?: boolean;
  /** When true, sections render fully expanded with no accordion chrome. */
  flat?: boolean;
  /** When true, omit the large sticky title (page already has its own heading). */
  hideTitle?: boolean;
}

interface EditorFrameContextType {
  onSubmit: (data: { [x: string]: any }) => Promise<void>;
  accordionValue: string[];
  setAccordionValue: Dispatch<SetStateAction<string[]>>;
}

export const EditorFrameContext = React.createContext<EditorFrameContextType>({
  onSubmit: async (_data: { [x: string]: any }) => {},
  accordionValue: [],
  setAccordionValue: () => {},
});

export const useEditorFrameContext = () => {
  const context = React.useContext(EditorFrameContext);
  if (!context) {
    throw new Error("useEditorFrameContext must be used within an EditorFrameContext.Provider");
  }
  return context;
};

export const EditorFrame: React.FC<EditorFrameProps> = (props) => {
  const [isSaving, setIsSaving] = useState(false);
  const { AdditionalButtons, disableDefaultRedirect = false, hideSave = false, flat = false, hideTitle = false } =
    props;

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { dialog, dismiss } = useDialog();
  const { t } = useTranslation();

  const startingAccordionValue = useMemo(() => {
    if (flat) return [];
    const allAccordionTitles: string[] =
      React.Children.map(props.children ?? [], (child) => {
        if (React.isValidElement(child) && child.props.title) {
          return child.props.title;
        }
        return null;
      })?.filter(Boolean) ?? [];
    return allAccordionTitles;
  }, [props.children, flat]);

  const [accordionValue, setAccordionValue] = React.useState<string[]>(startingAccordionValue);

  const form = useForm({
    mode: "onChange",
    defaultValues: React.useMemo(() => props.defaultValues, [props.defaultValues]),
  });

  React.useEffect(() => {
    form.reset(props.defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultValues]);

  const alwaysIncludeKeys = ["Id", "Key", "CurrencyId", "$op", "SortOrder"];
  function alwaysInclude(values: any, dirtyValues: any) {
    alwaysIncludeKeys.forEach((key) => {
      if (key in values) {
        dirtyValues[key] = values[key];
      }
    });
  }

  function getDirtyValues(values: any, dirtyFields: any): any {
    if (dirtyFields === true) {
      return values;
    }
    if (Array.isArray(dirtyFields)) {
      return dirtyFields
        .map((dirty, index) => {
          if (dirty) {
            return getDirtyValues(values[index], dirty);
          } else {
            return undefined;
          }
        })
        .filter((v) => v !== undefined);
    }
    if (typeof dirtyFields === "object" && dirtyFields !== null) {
      const dirtyValues: any = {};
      for (const key in dirtyFields) {
        if (dirtyFields[key]) {
          dirtyValues[key] = getDirtyValues(values[key], dirtyFields[key]);
        }
      }
      alwaysInclude(values, dirtyValues);
      return dirtyValues;
    }
    return undefined;
  }

  const cleanupBadData = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj
        .map((item) => cleanupBadData(item))
        .filter((item) => !(Array.isArray(item) && item.length === 0));
    } else if (typeof obj === "object" && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj)
          .map(([key, value]) => {
            let cleanedValue = cleanupBadData(value);
            if (key.endsWith("Date") && cleanedValue === "") {
              cleanedValue = null;
            }
            return [key, cleanedValue];
          })
          .filter(([_, value]) => !(Array.isArray(value) && value.length === 0)),
      );
    }
    return obj;
  };

  const onSubmit = async (data: { [x: string]: any }) => {
    let dirtyValues = getDirtyValues(data, form.formState.dirtyFields);
    dirtyValues = cleanupBadData(dirtyValues);
    try {
      setIsSaving(true);
      const result = await props.onSave(dirtyValues);
      setIsSaving(false);
      console.info(result);
      toast({
        title: t("ui.saveSuccessful", "Save Successful"),
        description: t("ui.yourChangesHaveBeenSaved", "Your changes have been saved"),
      });
      if (!disableDefaultRedirect) {
        navigate(pathname, { replace: true, preventScrollReset: true });
      }
    } catch (err) {
      setIsSaving(false);
      dialog({
        title: t("ui.error", "Error"),
        description: (err as Error)?.message || t("ui.unknownErrorOccurred", "An unknown error occurred"),
      });
    }
  };

  const onDelete = () => {
    const isDeleted = form.getValues("IsDeleted");
    dialog({
      title: isDeleted ? t("ui.restore", "Restore") : t("ui.delete", "Delete"),
      description: (
        <span className="mt-4 space-y-4">
          <span className="text-base mt-2 text-black">
            {isDeleted
              ? t("ui.areYouSureYouWantToRestoreThisItem", "Are you sure you want to restore this item?")
              : t("ui.areYouSureYouWantToDeleteThisItem", "Are you sure you want to delete this item?")}
          </span>
          {props.deleteDialogNote && <p className="text-xs">{props.deleteDialogNote}</p>}
          <span className="flex justify-between">
            <Button type="button" variant="outline" onClick={() => dismiss()}>
              {t("ui.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant={isDeleted ? "success" : "danger"}
              onClick={() => {
                form.setValue("IsDeleted", !isDeleted, { shouldDirty: true });
                form.handleSubmit(onSubmit)();
                dismiss();
              }}
            >
              {isDeleted ? t("ui.restore", "Restore") : t("ui.confirm", "Confirm")}
            </Button>
          </span>
        </span>
      ),
    });
  };

  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <EditorFrameContext.Provider value={{ onSubmit, accordionValue, setAccordionValue }}>
      <FormProvider {...form}>
        <Form
          onSubmit={({ data }) => onSubmit(data)}
          control={form.control}
          encType="application/json"
          method="post"
        >
          <div
            className={cn(
              "sticky top-0 z-40 bg-white/95 dark:bg-black/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-black/70 border-b border-surface-200 dark:border-surface-800",
              scrolled && "shadow-sm",
            )}
          >
            <div className="flex items-center justify-between px-0">
              <div>
                {!hideTitle && (
                  <h1 className="dark:text-white text-5xl font-bold py-6 m-0">{props.title}</h1>
                )}
                {props.subtitle && (
                  <p className="text-gray-600 dark:text-gray-400 -mt-4 mb-4">{props.subtitle}</p>
                )}
                {hideTitle && <div className="py-3" />}
              </div>
              <div className="gap-2 flex flex-wrap md:flex-nowrap py-6">
                {props.onCancel && (
                  <Button type="button" variant="outline" onClick={props.onCancel}>
                    {t("ui.cancel", "Cancel")}
                  </Button>
                )}
                {AdditionalButtons && <AdditionalButtons />}
                {!hideSave && (
                  <Button type="submit" variant="success" disabled={!form.formState.isDirty}>
                    {isSaving && <Spinner className="mr-2" />}
                    {t("ui.Save", "Save")}
                  </Button>
                )}
                {!props.hideDelete && (
                  <Button
                    type="button"
                    variant={form.getValues("IsDeleted") ? "success" : "danger"}
                    onClick={onDelete}
                  >
                    {form.getValues("IsDeleted") ? t("ui.restore", "Restore") : t("ui.delete", "Delete")}
                  </Button>
                )}
              </div>
            </div>
            {!flat && (
              <div className="flex space-x-2 justify-end px-0 pb-4 -mt-4">
                <Button type="button" onClick={() => setAccordionValue([])}>
                  {t("ui.collapseAll", "Collapse All")}
                </Button>
                <Button type="button" onClick={() => setAccordionValue(startingAccordionValue)}>
                  {t("ui.expandAll", "Expand All")}
                </Button>
              </div>
            )}
          </div>
          {flat ? (
            <div
              className={cn(
                "mt-4 rounded-container border border-surface-200 dark:border-surface-800 divide-y divide-surface-200 dark:divide-surface-800",
                props.classNames,
              )}
            >
              {React.Children.map(props.children, (child) => {
                if (!React.isValidElement(child)) return child;
                // Force non-collapsible layout for every section in flat mode.
                return React.cloneElement(child as React.ReactElement<any>, { collapsible: false });
              })}
            </div>
          ) : (
            <Accordion
              value={accordionValue}
              onValueChange={setAccordionValue}
              type="multiple"
              className={cn(
                "mt-4 rounded-container border border-surface-200 dark:border-surface-800",
                props.classNames,
              )}
            >
              {props.children}
            </Accordion>
          )}
        </Form>
      </FormProvider>
    </EditorFrameContext.Provider>
  );
};

interface EditorSectionProps extends React.PropsWithChildren {
  title: string;
  classNames?: string;
  collapsible?: boolean;
}

export const EditorSection: React.FC<EditorSectionProps> = ({ title, children, classNames, collapsible = true }) => {
  if (!collapsible) {
    return (
      <div className={cn("overflow-visible", classNames)}>
        <div className="px-4 py-3 font-bold text-lg text-[#0F2942] bg-gray-50">{title}</div>
        <div className="p-6 bg-white dark:bg-black overflow-visible">{children}</div>
      </div>
    );
  }

  return (
    <AccordionItem
      value={title}
      className={cn("border-surface-200 dark:border-surface-800 overflow-visible", classNames)}
    >
      <AccordionTrigger className="p-4 font-bold text-xl bg-surface-100 dark:bg-surface-900 hover:no-underline">
        {title}
      </AccordionTrigger>
      <AccordionContent className="p-6 bg-white dark:bg-black overflow-visible">{children}</AccordionContent>
    </AccordionItem>
  );
};
